<template>
    <div
        data-testid="document-discussion"
        :class="containerClass"
    >
        <div
            v-if="stickToBottom"
            class="grow"
        ></div>
        <StickyContainer
            :offset="stickToBottom ? 0 : viewerOffset"
            :position="stickToBottom ? 'bottom' : 'top'"
            :class="[
                'flex flex-col',
                isShowingDiscussion === false ? 'w-0 justify-end' : 'h-full overflow-hidden pr-7 md:pr-4 lg:pr-10',
            ]"
            :style="{
                maxHeight,
            }"
        >
            <transition
                v-if="isShowingDiscussion !== false && screen.minMD"
                name="slide-left"
                :css="!prefersReducedMotion"
                appear
                @before-enter="$store.commit('document/SET_SHOW_DISCUSSION', true)"
                @after-enter="onDiscussionTransitionFinished"
                @after-leave="$store.commit('document/SET_SHOW_DISCUSSION', false)"
            >
                <ScrollContainer
                    v-show="isShowingDiscussion"
                    :ref="isScreenNarrow ? 'scroll' : undefined"
                    :disabled="!isScreenNarrow"
                    class="hidden md:flex h-full flex-col pt-4 -mr-3"
                    style="width: 400px"
                    @scroll="updateScrollDistance"
                >
                    <div class="relative ml-8 mr-3 z-1">
                        <div
                            class="discussion-curtain absolute bottom-0 h-6 w-full -mt-2 -mr-3 -mb-4 flex-shrink-1"
                        ></div>
                        <PortalTarget name="document-discussion-create-post" />
                        <button
                            class="collapse-button -ml-7 absolute flex justify-center items-center py-2 w-7 h-17 left-0 top-0 bg-white shadow rounded-l-lg"
                            @click="toggleDiscussion"
                        >
                            <i class="icon icon-chevron-right"></i>
                        </button>
                    </div>
                    <PortalTarget name="document-discussion" />
                </ScrollContainer>
            </transition>

            <transition
                v-if="isShowingDiscussion !== true"
                name="fade"
                :css="!prefersReducedMotion"
                appear
                @after-leave="$store.commit('document/SET_SHOW_DISCUSSION', true)"
            >
                <div
                    v-if="isShowingDiscussion === false"
                    class="my-20 flex justify-end"
                >
                    <div class="pr-7 flex flex-col items-end">
                        <BtnCta
                            icon="discussions"
                            :label="t('show.fab.discussion')"
                            label-on-touch
                            @click="toggleDiscussion"
                        ></BtnCta>
                        <BtnCta
                            icon="edit"
                            :label="t('show.fab.createPost')"
                            label-on-touch
                            class="mt-4"
                            @click="triggerPostCreate"
                        ></BtnCta>
                    </div>
                </div>
            </transition>
        </StickyContainer>

        <transition @after-enter="onDiscussionTransitionFinished">
            <ModalFullscreen
                v-show="isShowingDiscussionModal"
                class="document-discussion-modal"
                :title="t('show.title_discussion')"
                @close="() => screen.maxSM && toggleDiscussion()"
            >
                <template #cta>
                    <transition name="fade">
                        <Btn
                            v-show="showCreatePostIcon"
                            variant="text"
                            compact
                            icon="edit"
                            :title="t('show.fab.createPost')"
                            @click.stop="triggerPostCreate"
                        />
                    </transition>
                </template>
                <PortalTarget name="document-discussion-overlay" />
            </ModalFullscreen>
        </transition>

        <portal :to="`document-discussion${screen.maxSM ? '-overlay' : ''}`">
            <!-- native modifier has been removed, please confirm whether the function has been affected  -->
            <ScrollContainer
                :ref="isScreenNarrow && screen.minMD ? undefined : 'scroll'"
                :disabled="isScreenNarrow && screen.minMD"
                class="md:pl-8 md:w-full h-full md:pr-3"
                @scroll="updateScrollDistance"
            >
                <PortalTarget name="document-discussion-overlay-create-post" />
                <transition-group
                    id="discussion-container"
                    tag="div"
                    name="fade"
                    class="pt-4 pb-1"
                >
                    <PostItem
                        v-for="post in posts"
                        :ref="`post${post.postDetails.id}`"
                        :key="post.postDetails.id"
                        :post="post"
                        hide-help-button
                        :selected-post="selectedPost"
                        class="mb-4"
                        @scroll-to-mark="scrollToDocumentMarking"
                    ></PostItem>
                    <Spinner
                        v-if="isDiscussionLoading"
                        appearance="dots"
                        size="xs"
                    ></Spinner>
                </transition-group>
            </ScrollContainer>
        </portal>

        <!-- we need to inject the freate post form via portals for the different views to keep one component reference and retain its state -->
        <portal :to="`document-discussion${screen.maxSM ? '-overlay' : ''}-create-post`">
            <PostCreate
                ref="createForm"
                :context-id="documentId"
                :show-buttons-label="screen.maxSM"
                context-type="document"
                class="mt-0"
                @created="onPostCreated"
            ></PostCreate>
        </portal>
    </div>
</template>

<script>
import { inject, defineAsyncComponent } from 'vue';
import PostCreate from '@components/PostCreate';
import PostItem from '@components/PostItem';
import { mapActions, mapGetters } from 'vuex';
import StickyContainer from '@/components/StickyContainer';
import ScrollContainer from '@components/ScrollContainer';
import { provideViewerOffsetKey } from '@/components/keys';
import { smoothScrollTop, getOffsetTop } from '@helpers/dom';
import { useTranslation } from 'i18next-vue';

export default {
    name: 'DocumentShowDiscussion',
    components: {
        PostCreate,
        PostItem,
        StickyContainer,
        ScrollContainer,
        ModalFullscreen: defineAsyncComponent(() => import('@/components/ModalFullscreen')),
    },
    props: {
        documentId: {
            type: Number,
            required: true,
        },
        documentInfected: {
            type: Boolean,
            required: false,
        },
        selectedMarkingArea: {
            type: Object,
            default: () => ({
                id: null,
                marking: null,
            }),
        },
    },
    emits: ['post-created', 'update:selected-marking-area'],
    setup() {
        const { t } = useTranslation('document');
        return {
            viewerOffset: inject(provideViewerOffsetKey),
            t
        };
    },
    data() {
        return {
            selectedPost: null,
            discussionScrollDistance: 0,
            discussionToggleCallback: null,
            isFromSeeMarkAction: false,
        };
    },
    computed: {
        ...mapGetters({
            isDiscussionLoading: 'document/currentDocumentDiscussionLoading',
            isDiscussionComplete: 'document/currentDocumentDiscussionComplete',
            posts: 'document/currentDocumentDiscussionPosts',
            isShowingDiscussion: 'document/isShowingDiscussion',
            isLoggedIn: 'auth/isLoggedIn',
        }),
        ...mapGetters('post', ['createPopupIsOpen']),
        ...mapGetters('ui', [
            'screen',
            'windowSize',
            'prefersReducedMotion',
        ]),

        maxHeight() {
            return `calc(100vh - ${this.viewerOffset}px)`;
        },
        stickyToggleBindings() {
            if (this.screen.maxSM) {
                return {
                    offset: 0,
                    position: 'bottom',
                    class: 'py-4',
                };
            }

            return {
                offset: this.viewerOffset,
                position: 'top',
                class: 'pt-4',
            };
        },
        isShowingDiscussionModal() {
            return this.isShowingDiscussion && this.screen.maxSM;
        },
        showCreatePostIcon() {
            return this.discussionScrollDistance >= 125;
        },
        isScreenNarrow() {
            return this.windowSize.height < 800;
        },
        stickToBottom() {
            return this.isShowingDiscussion === false && this.screen.maxSM;
        },
        containerClass() {
            return [
                {
                    'z-[8]': this.isShowingDiscussion && !this.isScreenNarrow && !this.screen.maxSM,
                    'z-[7]': !this.screen.maxSM && this.isScreenNarrow && !this.createPopupIsOpen,
                    'z-[41]': this.screen.maxSM && this.createPopupIsOpen,
                    'z-[11]': this.isScreenNarrow && this.createPopupIsOpen,
                },
                this.isShowingDiscussion === false ? 'flex flex-col' : 'md:-ml-4 lg:-ml-10',
            ];
        },
    },
    watch: {
        async selectedMarkingArea(newValue) {
            if (newValue?.id > 0) {
                if (!(await this.isPostLoaded(newValue.id))) {
                    await this.loadPost(newValue.id);
                    await this.$nextTick();
                }
                if (!this.isFromSeeMarkAction) {
                    this.toggleDiscussion(true, () => this.scrollToPost(newValue.id));
                }
                this.isFromSeeMarkAction = false;
            }
        },
        isShowingDiscussion(val) {
            // we want to disable scrolling on small screens when the discussion is open
            this.$store.dispatch('ui/setScrollingEnabled', val !== true || this.screen.minMD);
        },
        discussionScrollDistance(val) {
            const scrollEl = this.$refs.scroll?.getScrollElem();
            if (!scrollEl) return;

            // check threshold before scroll content reaches end
            if (val + scrollEl.clientHeight >= scrollEl.scrollHeight - this.windowSize.height) {
                this.loadNextPage();
            }
        },
        isShowingDiscussionModal() {
            // the scrollbox resets when switching from medium to small screens, so we need to re-compute the distance
            this.updateScrollDistance();
        },
    },
    mounted() {
        this.toggleDiscussion(this.screen.minMD);
    },
    methods: {
        ...mapActions({
            isPostLoaded: 'document/isPostLoaded',
            loadPost: 'document/loadPost',
        }),
        updateScrollDistance() {
            this.discussionScrollDistance = this.$refs.scroll?.getScrollElem()?.scrollTop || 0;
        },
        loadNextPage() {
            if (this.isDiscussionLoading || this.isDiscussionComplete) {
                return;
            }

            this.$store.dispatch('document/loadNextDiscussionPage');
        },
        addPostToDiscussion(post) {
            this.$store.dispatch('document/addPostToDiscussion', post);
        },
        onPostCreated(post) {
            this.addPostToDiscussion(post);

            const { markings } = post.postDetails;
            if (markings) {
                this.$emit('post-created', markings);
            }
        },
        triggerPostCreate() {
            this.toggleDiscussion(true, () => this.$refs.createForm.trigger());
        },
        async scrollToDocumentMarking(marking) {
            if (this.documentInfected) {
                return;
            }

            this.isFromSeeMarkAction = true;
            this.toggleDiscussion(this.screen.minMD, () => {
                this.selectedPost = marking.id;
                this.$emit('update:selected-marking-area', marking);
            });
            if (this.screen.maxSM) {
                this.onDiscussionTransitionFinished();
            }
        },
        /**
         * @returns {Promise}
         */
        scrollDiscussionIntoView() {
            if (this.screen.maxSM) return Promise.resolve();

            const wrapperEl = this.$el.parentElement;

            const minOffset = wrapperEl.offsetTop - this.viewerOffset;
            const maxOffset = wrapperEl.offsetTop + wrapperEl.offsetHeight;

            if (window.scrollY < minOffset) {
                // scroll to start of document to fully display discussion
                return smoothScrollTop(window, minOffset);
            }

            if (window.scrollY + window.innerHeight > maxOffset) {
                // scroll to end of document to fully display discussion
                return smoothScrollTop(
                    window,
                    maxOffset - this.viewerOffset - this.$refs.discussionWrapper.$el.offsetHeight,
                );
            }

            return Promise.resolve();
        },
        async scrollToPost(postId) {
            this.selectedPost = parseInt(postId, 10);
            const breath = 20;

            const pageScrollPromise = this.scrollDiscussionIntoView();

            /* try getting a post's ref 5 times */
            let [postRef] = this.$refs[`post${postId}`];
            let attempts = 1;
            while (!postRef && attempts < 5) {
                // eslint-disable-next-line no-await-in-loop
                await this.$nextTick();
                [postRef] = this.$refs[`post${postId}`];
                attempts += 1;
            }

            if (!postRef) return Promise.resolve();

            // then scroll to post in discussion
            return Promise.all([
                pageScrollPromise,
                this.$refs.scroll?.scroll({
                    top: getOffsetTop(postRef.$el, this.$refs.scroll.getScrollElem()) - breath,
                }),
            ]);
        },
        /**
         * @param {Function} - callback that will be triggered after the discussion has finished its transition animation
         * @param {Boolean} - (optional) value to toggle
         */
        toggleDiscussion(showDiscussion = null, callback = null) {
            if (showDiscussion !== null && this.isShowingDiscussion === showDiscussion) {
                callback?.();
                return;
            }
            this.discussionToggleCallback = callback;
            this.$store.dispatch('document/toggleDiscussion');
        },
        onDiscussionTransitionFinished() {
            this.discussionToggleCallback?.();
            this.discussionToggleCallback = null;
        },
    },
};
</script>

<style lang="scss" scoped>
.slide-left-enter-active,
.slide-left-leave-active {
    transition: transform 0.5s ease-in-out;
}

.slide-left-enter-from,
.slide-left-leave-to {
    transform: translateX(calc(100% + 40px));
}

.fade-leave-active {
    transition: none !important;
}

.fade-enter-active {
    transition-duration: 0.4s !important;
}

.collapse-button::after {
    @apply absolute h-full w-4 right-0 -mr-2 bg-white;
    content: '';
}

.discussion-curtain {
    background: linear-gradient(180deg, theme('colors.slate.50') 35%, rgba(var(--colors-slate-50) / 2%) 100%);
}
</style>
